import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  TrendingUp, TrendingDown, Minus, Activity, Anchor,
  Clock, Wallet, Target, Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_API_URL}/api`;
const heading = { fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif" };

// Compute health score: weighted average of 4 normalized KPIs (0-100).
const computeHealthScore = ({ occupancyPct, leadTime, conversionPct, revenuePerBoat }) => {
  // Each component normalized to 0-100
  const cOccupancy = Math.min(100, occupancyPct * 1.4); // 70% occupancy -> ~98
  const cLead = Math.min(100, leadTime > 0 ? Math.min(leadTime, 30) * 3.3 : 0); // 30g -> 100
  const cConv = Math.min(100, conversionPct * 1.3); // 75% -> 97
  const cRev = revenuePerBoat > 0 ? Math.min(100, (revenuePerBoat / 4000) * 100) : 0; // €4k/boat -> 100
  return Math.round(0.35 * cOccupancy + 0.2 * cLead + 0.2 * cConv + 0.25 * cRev);
};

const Trend = ({ delta, suffix = "" }) => {
  if (delta === null || delta === undefined) return <span className="text-xs text-slate-500">—</span>;
  const positive = delta > 0;
  const negative = delta < 0;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const color = positive ? "text-emerald-400" : negative ? "text-red-400" : "text-slate-500";
  const sign = positive ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
      <Icon className="w-3 h-3" />
      {sign}{Math.abs(delta).toFixed(0)}{suffix}
    </span>
  );
};

const Metric = ({ icon: Icon, color, label, value, suffix, delta, deltaSuffix, hint, testId }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-7 h-7 rounded-lg ${color} bg-opacity-15 flex items-center justify-center`}>
        <Icon className={`w-3.5 h-3.5 ${color.replace("bg-", "text-")}`} />
      </div>
      <span className="text-xs text-slate-400 font-medium">{label}</span>
    </div>
    <div className="flex items-baseline justify-between mb-1">
      <p className="text-2xl font-bold text-white tabular-nums" style={heading}>
        {value}<span className="text-base font-normal text-slate-400 ml-0.5">{suffix}</span>
      </p>
      <Trend delta={delta} suffix={deltaSuffix || ""} />
    </div>
    {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
  </div>
);

const ScoreBadge = ({ score }) => {
  const tier =
    score >= 80 ? { label: "Eccellente", color: "text-emerald-300", bg: "bg-emerald-500/15", ring: "stroke-emerald-400" }
    : score >= 60 ? { label: "Buono", color: "text-blue-300", bg: "bg-blue-500/15", ring: "stroke-blue-400" }
    : score >= 40 ? { label: "Da migliorare", color: "text-amber-300", bg: "bg-amber-500/15", ring: "stroke-amber-400" }
    : { label: "Critico", color: "text-red-300", bg: "bg-red-500/15", ring: "stroke-red-400" };
  const dash = (score / 100) * 282.74; // circumference 2πr (r=45)
  return (
    <div className="flex items-center gap-5">
      <div className="relative w-24 h-24" data-testid="health-score-ring">
        <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" className="stroke-white/10" strokeWidth="8" fill="none" />
          <circle
            cx="50" cy="50" r="45"
            className={tier.ring}
            strokeWidth="8"
            fill="none"
            strokeDasharray={`${dash} 282.74`}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.16, 1, 0.3, 1)" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-white tabular-nums" style={heading} data-testid="health-score-value">{score}</span>
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">su 100</span>
        </div>
      </div>
      <div>
        <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${tier.bg} ${tier.color}`} data-testid="health-score-tier">
          {tier.label}
        </span>
        <p className="text-sm text-white font-semibold mt-2" style={heading}>Indice di salute del tuo charter</p>
        <p className="text-xs text-slate-400 max-w-xs mt-0.5">Aggiornato in tempo reale dai tuoi dati operativi.</p>
      </div>
    </div>
  );
};

export const HealthScoreCard = ({ token }) => {
  const [data, setData] = useState({ bookings: [], boats: [], loading: true, error: null });

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.allSettled([
      axios.get(`${API}/bookings`, { headers }),
      axios.get(`${API}/boats`, { headers }),
    ]).then(([bRes, sRes]) => {
      const bookings = bRes.status === "fulfilled" && Array.isArray(bRes.value.data) ? bRes.value.data : [];
      const boats = sRes.status === "fulfilled" && Array.isArray(sRes.value.data) ? sRes.value.data : [];
      setData({ bookings, boats, loading: false, error: null });
    }).catch(() => setData({ bookings: [], boats: [], loading: false, error: "load" }));
  }, [token]);

  const metrics = useMemo(() => {
    const { bookings, boats } = data;
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    const inRange = (dStr, a, b) => {
      if (!dStr) return false;
      const d = new Date(dStr);
      return d >= a && d <= b;
    };

    const confirmed = (b) => ["confirmed", "completed", "paid"].includes((b.booking_status || "").toLowerCase());
    const requestPending = (b) => ["pending", "request"].includes((b.booking_status || "").toLowerCase());

    const thisMonth = bookings.filter(b => inRange(b.booking_date, thisMonthStart, thisMonthEnd));
    const lastMonth = bookings.filter(b => inRange(b.booking_date, lastMonthStart, lastMonthEnd));

    // 1. Occupancy: (confirmed bookings / (boats * days in month))
    const days = thisMonthEnd.getDate();
    const boatCount = Math.max(boats.length, 1);
    const occThis = (thisMonth.filter(confirmed).length / (boatCount * days)) * 100;
    const occLast = (lastMonth.filter(confirmed).length / (boatCount * lastMonthEnd.getDate())) * 100;

    // 2. Lead time: avg days between created_at and booking_date for confirmed
    const lt = (arr) => {
      const vals = arr.filter(confirmed).map(b => {
        if (!b.created_at || !b.booking_date) return null;
        const ms = new Date(b.booking_date) - new Date(b.created_at);
        return ms > 0 ? ms / (1000 * 60 * 60 * 24) : null;
      }).filter(v => v !== null);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    };
    const ltThis = lt(thisMonth);
    const ltLast = lt(lastMonth);

    // 3. Revenue per boat (this month)
    const rev = (arr) => arr.filter(confirmed).reduce((sum, b) => sum + (Number(b.total_amount) || 0), 0);
    const revThis = rev(thisMonth);
    const revLast = rev(lastMonth);
    const revPerBoatThis = revThis / boatCount;
    const revPerBoatLast = revLast / boatCount;

    // 4. Conversion rate: confirmed / (confirmed + pending) all-time
    const totalConfirmed = bookings.filter(confirmed).length;
    const totalPending = bookings.filter(requestPending).length;
    const convThis = (totalConfirmed + totalPending) > 0
      ? (totalConfirmed / (totalConfirmed + totalPending)) * 100
      : 0;

    const score = computeHealthScore({
      occupancyPct: occThis,
      leadTime: ltThis,
      conversionPct: convThis,
      revenuePerBoat: revPerBoatThis,
    });

    return {
      score,
      boatCount: boats.length,
      hasData: bookings.length > 0,
      occupancy: { value: Math.round(occThis), delta: Math.round(occThis - occLast) },
      leadTime: { value: Math.round(ltThis), delta: Math.round(ltThis - ltLast) },
      revenue: { value: Math.round(revPerBoatThis), delta: Math.round(revPerBoatThis - revPerBoatLast) },
      conversion: { value: Math.round(convThis), delta: null },
    };
  }, [data]);

  if (data.loading) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-24 h-24 rounded-full bg-white/5"></div>
          <div className="space-y-2">
            <div className="h-4 w-40 bg-white/5 rounded"></div>
            <div className="h-3 w-56 bg-white/5 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics.hasData) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6" data-testid="health-empty">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-white">Indice di salute</h2>
        </div>
        <p className="text-sm text-slate-400">
          Quando ricevi le prime prenotazioni, qui troverai un indice di salute del tuo charter (occupazione, lead time, revenue per barca, tasso di conversione) con confronto mese su mese.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6" data-testid="health-score-card">
      <ScoreBadge score={metrics.score} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        <Metric
          icon={Activity}
          color="bg-blue-400"
          label="Occupazione"
          value={metrics.occupancy.value}
          suffix="%"
          delta={metrics.occupancy.delta}
          deltaSuffix="pp"
          hint="vs mese scorso"
          testId="metric-occupancy"
        />
        <Metric
          icon={Clock}
          color="bg-violet-400"
          label="Lead time medio"
          value={metrics.leadTime.value}
          suffix="g"
          delta={metrics.leadTime.delta}
          deltaSuffix="g"
          hint="anticipo prenotazioni"
          testId="metric-leadtime"
        />
        <Metric
          icon={Wallet}
          color="bg-emerald-400"
          label="Revenue/barca"
          value={`€${metrics.revenue.value.toLocaleString("it-IT")}`}
          delta={metrics.revenue.delta ? Math.round((metrics.revenue.delta / Math.max(1, metrics.revenue.value - metrics.revenue.delta)) * 100) : 0}
          deltaSuffix="%"
          hint="questo mese"
          testId="metric-revenue"
        />
        <Metric
          icon={Target}
          color="bg-amber-400"
          label="Tasso conversione"
          value={metrics.conversion.value}
          suffix="%"
          delta={null}
          hint="richieste → confermate"
          testId="metric-conversion"
        />
      </div>

      <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-xs text-slate-500">
        <Anchor className="w-3.5 h-3.5" />
        <span>Calcolato su {metrics.boatCount} barca{metrics.boatCount === 1 ? "" : "che"} attive · aggiornato ora</span>
      </div>
    </div>
  );
};
